// Fenêtre commune : quand nous travaillons tous les deux, et combien de temps il reste.
// Rend visible le coût en jours : une question envoyée après la fermeture de la fenêtre
// n’obtient sa réponse qu’à la prochaine ouverture.
import { DAY, MIN, dayStart, info, nextDate, todayIn } from './time';
import type { Hours } from './contact';

export interface Interval {
  start: number;
  end: number;
}

/** Instant d’une heure locale (minutes depuis minuit) pour une date d’un fuseau, changements d’heure compris. */
export function localInstant(date: string, minutes: number, tz: string): number {
  let t = dayStart(date, tz) + minutes * MIN;
  const p = info(tz, t);
  if (p.date !== date || p.minutes !== minutes) {
    let diff = p.minutes - minutes;
    if (p.date > date) diff += 1440;
    else if (p.date < date) diff -= 1440;
    t -= diff * MIN;
  }
  return t;
}

/** Périodes de travail, en instants, sur [from, from + days jours]. Horaires de nuit compris. Périodes contiguës fusionnées. */
export function workIntervals(h: Hours, from: number, days = 7): Interval[] {
  const to = from + days * DAY;
  const raw: Interval[] = [];
  let date = nextDate(todayIn(h.tz, from), -1);
  const last = todayIn(h.tz, to);
  while (date <= last) {
    const dow = new Date(date + 'T12:00:00Z').getUTCDay();
    if (h.days.includes(dow)) {
      const start = localInstant(date, h.start, h.tz);
      const end = h.start < h.end ? localInstant(date, h.end, h.tz) : localInstant(nextDate(date), h.end, h.tz);
      if (end > from && start < to) raw.push({ start: Math.max(start, from), end: Math.min(end, to) });
    }
    date = nextDate(date);
  }
  raw.sort((a, b) => a.start - b.start);
  const merged: Interval[] = [];
  for (const iv of raw) {
    const prev = merged[merged.length - 1];
    if (prev && iv.start <= prev.end) prev.end = Math.max(prev.end, iv.end);
    else merged.push({ ...iv });
  }
  return merged;
}

export function intersect(a: Interval[], b: Interval[]): Interval[] {
  const out: Interval[] = [];
  let i = 0, j = 0;
  while (i < a.length && j < b.length) {
    const A = a[i]!, B = b[j]!;
    const s = Math.max(A.start, B.start), e = Math.min(A.end, B.end);
    if (s < e) out.push({ start: s, end: e });
    if (A.end < B.end) i++; else j++;
  }
  return out;
}

export type CommonWindow =
  | { state: 'open'; until: number; next: Interval | null }
  | { state: 'later'; at: number; until: number }
  | { state: 'none' };

/** État de la fenêtre commune à l’instant `now`, en regardant jusqu’à `days` jours devant. */
export function commonWindow(me: Hours, other: Hours, now: number, days = 7): CommonWindow {
  const from = now - DAY;
  const common = intersect(workIntervals(me, from, days + 1), workIntervals(other, from, days + 1));
  const idx = common.findIndex((iv) => iv.end > now);
  if (idx < 0) return { state: 'none' };
  const iv = common[idx]!;
  if (iv.start <= now) return { state: 'open', until: iv.end, next: common[idx + 1] ?? null };
  return { state: 'later', at: iv.start, until: iv.end };
}

/** Durée lisible : '47 min', '2 h 10', '5 h'. */
export function durationText(ms: number): string {
  const m = Math.max(0, Math.round(ms / MIN));
  const h = Math.floor(m / 60), r = m % 60;
  if (h === 0) return `${m} min`;
  return r ? `${h} h ${String(r).padStart(2, '0')}` : `${h} h`;
}
